import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Radio from "@mui/material/Radio";
import { Controller, useFormContext } from "react-hook-form";
import { CHECKLIST_QUESTION_TYPE } from "../../constants";
import { useLocale } from "../../providers/LocaleProvider";
import RemoveIcon from "../../resources/images/icons-remove.svg";

interface OptionItemProps {
  optIndex: number;
  questionType?: string;
  name?: string;
  option?: any;
  onRemoveOption?: (optIndex: number) => void;
  canRemove?: boolean;
  triggerValidationValueDebounced?: () => void;
}

export default function OptionItem({
  optIndex,
  questionType,
  name,
  option,
  onRemoveOption,
  canRemove,
  triggerValidationValueDebounced
}: OptionItemProps) {
  const { translate } = useLocale();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.id
  });

  const { control } = useFormContext();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  return (
    <Box
      sx={{ display: "flex", alignItems: "flex-start" }}
      key={option.id}
      ref={setNodeRef}
      style={style}
    >
      <IconButton
        className='drag-handle'
        {...attributes}
        {...listeners}
        sx={{
          opacity: 1,
          transition: "opacity 0.2s ease-in-out",
          "&:hover": { backgroundColor: "#DBEAFE" },
          borderRadius: "5px",
          marginRight: 1,
          cursor: "grab",
          "&:active": { cursor: "grabbing" }
        }}
      >
        <DragIndicatorIcon fontSize='small' />
      </IconButton>
      <Controller
        control={control}
        name={`${name}.options.${optIndex}.optionText`}
        render={({ field, fieldState }) => (
          <FormControlLabel
            control={
              questionType === CHECKLIST_QUESTION_TYPE.SINGLE_CHOICE ? (
                <Radio size='small' disabled />
              ) : (
                <Checkbox size='small' disabled />
              )
            }
            label={
              <Box
                sx={{
                  alignItems: "flex-start",
                  gap: 1,
                  width: "100%",
                  display: "flex"
                }}
              >
                <InputBase
                  multiline
                  inputRef={field.ref}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    triggerValidationValueDebounced?.();
                  }}
                  placeholder={translate("settingsViews.checklists.optionPlaceholder", {
                    number: optIndex + 1
                  })}
                  sx={{
                    "& input": {
                      fontSize: "16px",
                      fontFamily: "Inter",
                      width: "100%"
                    },
                    borderRadius: "8px",
                    border: fieldState.error
                      ? "1px solid var(--torch-red-tr-500)"
                      : "1px solid transparent",
                    width: "100%",
                    padding: "6px 10px 6px 6px "
                  }}
                />
                {canRemove && (
                  <IconButton
                    className='remove-option-icon'
                    size='small'
                    onClick={() => onRemoveOption?.(optIndex)}
                    sx={{
                      transition: "opacity 0.2s ease-in-out",
                      "&:hover": { backgroundColor: "#DBEAFE" },
                      borderRadius: "5px"
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
            }
            sx={{
              width: "100%",
              "&:hover": {
                backgroundColor: "var(--grey-gr-050)",
                "& .remove-option-icon": { opacity: 1 },
                "& .drag-handle": { opacity: 1 }
              },
              position: "relative",
              "& .MuiFormControlLabel-label": {
                flex: 1
              },
              display: "flex",
              alignItems: "flex-start"
            }}
          />
        )}
      />
    </Box>
  );
}
